import { useEffect, useRef, useState } from "react";
import { Printer, Eye, ReceiptText } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt } from "@/components/Receipt";
import { SendInvoice } from "@/components/SendInvoice";
import { buildReceiptModel } from "@/lib/receipt";
import { formatMoney } from "@/lib/money";
import { api, errMsg } from "@/lib/api";
import { useAuth, useCan } from "@/context/AuthContext";
import { usePrint } from "@/context/PrintContext";
import { toast } from "sonner";
import { isWellFormedSale, kvGet, kvSet } from "@/lib/localdb";
import { onSynced, useOutbox } from "@/lib/outbox";

const fmtDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

// Outbox entries come from device storage and may be malformed: never let them break rendering.
function toLocalRow(i) {
  const ok = isWellFormedSale(i.sale);
  const s = i.sale && typeof i.sale === "object" ? i.sale : {};
  return {
    ...s, id: i.client_sale_id, client_sale_id: i.client_sale_id, created_at: s.created_at || i.created_at, receipt_code: s.receipt_code || "—",
    lines: Array.isArray(s.lines) ? s.lines : [], payment: s.payment || {}, totals: s.totals || {},
    _malformed: !ok, _local: ok ? i.status : "failed", _error: i.error || (ok ? "" : "Malformed sale — can't be uploaded"),
  };
}

function LocalTag({ s }) {
  if (!s._local) return null;
  return s._local === "failed" ? (
    <span className="rounded-full bg-rose-100 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-rose-700" title={s._error} data-testid={`sale-failed-tag-${s.id}`}>Sync failed</span>
  ) : (
    <span className="rounded-full bg-amber-100 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-800" data-testid={`sale-pending-tag-${s.id}`}>Pending sync</span>
  );
}

export default function Sales() {
  const { session } = useAuth();
  const { printSale } = usePrint();
  const [serverSales, setSales] = useState(null);
  const [view, setView] = useState(null);
  const can = useCan();
  const outbox = useOutbox();
  const reprint = async (s) => {
    if (s._local) return printSale(s);
    try {
      const { data } = await api.post(`/sales/${s.id}/reprint`);
      printSale(data);
    } catch (e) {
      if (!e.response) return printSale(s);
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    const key = `sales:${session.branch.id}`;
    const fetchSales = () =>
      api.get("/sales", { params: { limit: 50 } })
        .then((r) => {
          setSales(r.data);
          kvSet(key, r.data);
        })
        .catch(async (e) => {
          if (e.response) toast.error(errMsg(e));
          else setSales((await kvGet(key)) || []);
        });
    fetchSales();
    return onSynced(fetchSales);
  }, [session.branch.id]);

  const seen = useRef(new Map());
  const local = outbox.items.filter((i) => i.branch_id === session.branch.id).map(toLocalRow);
  const ids = new Set(local.map((s) => s.client_sale_id));
  const serverIds = new Set((serverSales || []).map((s) => s.client_sale_id).filter(Boolean));
  local.forEach((s) => !s._malformed && seen.current.set(s.client_sale_id, s));
  // Just-synced rows stay visible (without a tag) until the refreshed server list contains them.
  const synced = [...seen.current.values()].filter((s) => !ids.has(s.client_sale_id) && !serverIds.has(s.client_sale_id)).map((s) => ({ ...s, _local: null }));
  const rows = [...local, ...synced];
  const sales = serverSales === null && !rows.length ? null : [...rows, ...(serverSales || []).filter((s) => !ids.has(s.client_sale_id))];

  return (
    <div className="h-full overflow-y-auto" data-testid="sales-page">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-8">
        <div className="eyebrow">Register history</div>
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Recent sales</h1>
        <p className="mt-1 text-sm text-slate-500">Latest 50 receipts from {session.branch.name}. Reprint any of them.</p>
        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {sales === null ? (
            <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
          ) : sales.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center text-slate-400" data-testid="sales-empty">
              <ReceiptText className="h-8 w-8" /> No sales yet. Ring one up on the register.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="sales-list">
              {sales.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 sm:px-5" data-testid={`sale-row-${s.receipt_code}`}>
                  <div className="min-w-[130px]">
                    <div className="font-mono text-sm font-bold text-slate-900" data-testid={`sale-receipt-no-${s.id}`}>{s.receipt_code}</div>
                    <div className="text-xs text-slate-500">{fmtDate(s.created_at)} · {s.lines.reduce((a, l) => a + (Number(l?.qty) || 0), 0)} items{s.lines.some((l) => l?.returned_qty) && <span className="ml-1 font-semibold text-rose-600" data-testid={`sale-returned-${s.id}`}>· {s.lines.reduce((a, l) => a + (Number(l?.returned_qty) || 0), 0)} returned</span>}</div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${s.payment.method === "cash" ? "bg-emerald-100 text-emerald-800" : s.payment.method ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-500"}`} data-testid={`sale-method-${s.id}`}>
                    {s.payment.method || "—"}
                  </span>
                  <LocalTag s={s} />
                  {s.seller_disabled_at_sync && session.user.role === "owner" && <span className="rounded-full bg-slate-200 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-700" title="Made offline by a user who was disabled before it synced" data-testid={`sale-seller-disabled-tag-${s.id}`}>Seller disabled</span>}
                  {s._local === "failed" && <span className="w-full text-xs font-medium text-rose-700 sm:order-last" data-testid={`sale-failed-reason-${s.id}`}>{s._error}</span>}
                  <div className="money ml-auto text-right text-base font-bold text-slate-900" data-testid={`sale-total-${s.id}`}>
                    {typeof s.totals.total === "number" ? <><span className="mr-1 text-[10px] text-slate-400">{s.currency}</span>{formatMoney(s.totals.total, s.decimals)}</> : "—"}
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => setView(s)} disabled={s._malformed} data-testid={`sale-view-${s.id}`} className="grid h-11 w-11 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:border-slate-900 disabled:opacity-30" aria-label="View">
                      <Eye className="h-4 w-4" />
                    </button>
                    {can("reprint") && <button onClick={() => reprint(s)} disabled={s._malformed} data-testid={`sale-reprint-${s.id}`} className="flex h-11 items-center gap-1.5 rounded-lg bg-slate-900 px-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:bg-slate-300">
                      <Printer className="h-4 w-4" /> Reprint
                    </button>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-h-[92dvh] max-w-md overflow-y-auto" data-testid="sale-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Receipt {view?.receipt_code}</DialogTitle>
            <DialogDescription className="sr-only">Receipt preview</DialogDescription>
          </DialogHeader>
          {view && <div className="rounded-xl border border-slate-200 py-3"><Receipt model={buildReceiptModel(view, session.business)} /></div>}
          {view && <SendInvoice key={view.id} saleId={view.id} defaultPhone={view.customer_whatsapp || ""} branchId={view.branch_id} testid="sale-view-whatsapp" />}
          {can("reprint") && <button onClick={() => reprint(view)} data-testid="sale-view-print-button" className="flex h-12 items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white">
            <Printer className="h-4 w-4" /> Print
          </button>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
